import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AdvancedSelectForCreate from "./AdvancedSelectForCreate";
import AdvancedSelectForShipping from "./AdvancedSelectForShipping";
import { createSubscription } from "../api";
import useTaxList from "features/Taxe/TaxeList/useTaxList";
import useAllCategories from "features/Category/CategoryList/useAllCategories";
import { useGetAllExpeditionModeQuery } from "redux/api/delivery/expeditionMode/expeditionMode";
import { useGetAllExpeditionCategsQuery } from "redux/api/delivery/expeditionCategs/expeditionCategs";

const CreateSubscriptionModal = ({ isOpen, onClose }) => {
    const queryClient = useQueryClient();
    const { taxesData } = useTaxList();
    const { allCategories: categs } = useAllCategories();
    const taxes = taxesData?.data || [];

    const { data: shippingModesData } = useGetAllExpeditionModeQuery({ all: true });
    const { data: shippingCategoriesData } = useGetAllExpeditionCategsQuery({ all: true });

    const shippingModes = shippingModesData?.data || [];
    const shippingCategories = shippingCategoriesData?.data || [];

    const [formData, setFormData] = useState({
        label: "",
        description: "",
        type: "professionnel",
        duration_unit: "month",
        duration_value: 1,
        price_ht: 0,
        tva: taxes[0]?.id || 0,
        shipping_options: [],
        shipping_categ_ids: [],
        shipping_mode_ids: [],
        category_ids: [],
        discount_percentage_ship_cat: 0,
        discount_percentage_ship_mode: 0,
        is_free_ship_mode: false,
        is_free_ship_cat: false,
        visible: true
    });

    useEffect(() => {
        if (taxes.length > 0 && !formData.tva) {
            setFormData(prev => ({ ...prev, tva: taxes[0].id }));
        }
    }, [taxes]);
    console.log(formData)

    const createMutation = useMutation({
        mutationFn: createSubscription,
        onSuccess: () => {
            queryClient.invalidateQueries(['subscriptions']);
            toast.success("Abonnement créé avec succès");
            onClose();
        },
        onError: (error) => {
            toast.error(error.message || "Erreur lors de la création");
        }
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        createMutation.mutate(formData);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-auto p-4"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                aria-hidden="true"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className=" rounded-lg p-4 w-full max-w-3xl relative">
                <div
                    className="relative w-full max-w-2xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30 pb-4">
                        <h3
                            id="modal-title"
                            className="text-lg font-medium leading-6 text-white"
                        >
                            Créer un nouvel abonnement
                        </h3>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 hover:scale-105 duration-200"
                            onClick={onClose}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4 p-6 max-h-[80vh] overflow-auto">
                        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 gap-y-4 mt-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Label*</label>
                                <input
                                    type="text"
                                    name="label"
                                    value={formData.label}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description*</label>
                                <input
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Type*</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                >
                                    <option value="professionnel">Professionnel</option>
                                    <option value="particulier">Particulier</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Unité de durée*</label>
                                <select
                                    name="duration_unit"
                                    value={formData.duration_unit}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                >
                                    <option value="week">Semaine</option>
                                    <option value="month">Mois</option>
                                    <option value="year">Année</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Valeur de durée*</label>
                                <input
                                    type="number"
                                    name="duration_value"
                                    value={formData.duration_value}
                                    onChange={handleChange}
                                    min="1"
                                    required
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Prix HT*</label>
                                <input
                                    type="number"
                                    name="price_ht"
                                    value={formData.price_ht}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    required
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">TVA*</label>
                                <select
                                    name="tva"
                                    value={formData.tva}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12"
                                >
                                    {taxes?.map(tax => ({
                                        id: tax.id,
                                        name: tax.name,
                                        percentage: tax.percentage
                                    })).sort((a, b) => a.name.localeCompare(b.name)).map(tax => (
                                        <option key={tax.id} value={tax.id}>
                                            {tax.name} ({tax.percentage}%)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Catégories*</label>

                                <AdvancedSelectForCreate
                                    options={[...categs]
                                        .sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }))
                                        .map(c => c.title)
                                    }
                                    placeholder="Sélectionner des catégories"
                                    defaultValue={formData.category_ids.map(id => {
                                        const category = categs.find(c => c.id === id);
                                        return category ? category.title : null;
                                    }).filter(Boolean)}
                                    onChange={(selected) => setFormData(prev => ({
                                        ...prev,
                                        category_ids: selected.map(opt =>
                                            categs.find(c => c.title === opt)?.id
                                        ).filter(Boolean)
                                    }))}
                                    multiple
                                />
                                <div className="flex gap-2 mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            category_ids: categs.map(c => c.id)
                                        }))}
                                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                                    >
                                        Tout sélectionner
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            category_ids: []
                                        }))}
                                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                                    >
                                        Tout désélectionner
                                    </button>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg row-span-2 col-span-2">
                                <h4 className="text-lg font-medium text-gray-800 mb-4">Options d'expédition</h4>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Types d'expédition</label>
                                        <AdvancedSelectForShipping
                                            options={["Catégorie d'expédition", "Mode d'expédition"]}
                                            placeholder="Sélectionner des options"
                                            onChange={(selected) => setFormData(prev => ({
                                                ...prev,
                                                shipping_options: selected
                                            }))}
                                            multiple
                                        />
                                    </div>

                                    {formData.shipping_options.includes("Catégorie d'expédition") && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Catégories d'expédition*</label>
                                                <AdvancedSelectForShipping
                                                    options={[...shippingCategories]
                                                        .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
                                                        .map(cat => cat.name)
                                                    }
                                                    placeholder="Sélectionner des catégories d'expédition"
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        shipping_categ_ids: selected.map(opt =>
                                                            shippingCategories.find(c => c.name === opt)?.id
                                                        ).filter(Boolean)
                                                    }))}
                                                    multiple
                                                />
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Réduction (%)</label>
                                                    <input
                                                        type="number"
                                                        value={formData.discount_percentage_ship_cat}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            discount_percentage_ship_cat: Number(e.target.value)
                                                        }))}
                                                        min="0"
                                                        max="100"
                                                        disabled={formData.is_free_ship_cat}
                                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#b269ae] focus:border-[#b269ae] transition-all disabled:bg-gray-100"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.is_free_ship_cat}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            is_free_ship_cat: e.target.checked
                                                        }))}
                                                        className="h-4 w-4 text-[#b269ae] focus:ring-[#b269ae] border-gray-300 rounded"
                                                    />
                                                    <label className="text-sm text-gray-700">Gratuit</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {formData.shipping_options.includes("Mode d'expédition") && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Modes d'expédition*</label>
                                                <AdvancedSelectForShipping
                                                    options={[...shippingModes]
  .sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }))
  .map(mode => mode.title)
}

                                                    placeholder="Sélectionner des modes d'expédition"
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        shipping_mode_ids: selected.map(opt =>
                                                            shippingModes.find(m => m.title === opt)?.id
                                                        ).filter(Boolean)
                                                    }))}
                                                    multiple
                                                />
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Réduction (%)</label>
                                                    <input
                                                        type="number"
                                                        value={formData.discount_percentage_ship_mode}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            discount_percentage_ship_mode: Number(e.target.value)
                                                        }))}
                                                        min="0"
                                                        max="100"
                                                        disabled={formData.is_free_ship_mode}
                                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#b269ae] focus:border-[#b269ae] transition-all disabled:bg-gray-100"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.is_free_ship_mode}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            is_free_ship_mode: e.target.checked
                                                        }))}
                                                        className="h-4 w-4 text-[#b269ae] focus:ring-[#b269ae] border-gray-300 rounded"
                                                    />
                                                    <label className="text-sm text-gray-700">Gratuit</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>


                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="visible"
                                    checked={formData.visible}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-[#b269ae] focus:ring-[#b269ae] border-gray-300 rounded"
                                />
                                <label className="text-sm text-gray-700">Visible</label>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-6">
                            <button
                                type="submit"
                                // disabled={createMutation.isPending}
                                className="block rounded-md bg-[#b269ae] px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-yellow-300 duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 disabled:opacity-50"
                            >
                                {createMutation.isPending ? "Création..." : "Créer"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateSubscriptionModal;
